import { useState } from "react";
import { ArrowLeft, ScanBarcode, Keyboard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Input } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/input";

const BarcodeScanner = () => {
  const navigate = useNavigate();
  const [manualEntry, setManualEntry] = useState(false);
  const [barcode, setBarcode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleScan = (code: string) => {
    if (!code.trim()) {
      return;
    }
    setLoading(true);
    // Simulate lookup delay then navigate to result
    setTimeout(() => {
      navigate(`/barcode/result?code=${encodeURIComponent(code.trim())}`);
    }, 1200);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="bg-gradient-to-b from-primary/10 to-background px-4 pb-6 pt-8">
        <div className="mb-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Scan Barcode</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="space-y-6 px-4">
        {/* Scanner Area */}
        <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-primary/40 bg-muted">
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <ScanBarcode className="h-10 w-10 text-primary" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">
                Point at a barcode
              </p>
              <p className="text-sm text-muted-foreground">
                Align the barcode within the frame
              </p>
            </div>
          </div>
          {/* Scan line animation */}
          <div className="absolute inset-x-8 top-1/2 h-0.5 animate-pulse rounded-full bg-primary/60" />
          {/* Corner markers */}
          <div className="absolute left-4 top-4 h-8 w-8 rounded-tl-lg border-l-2 border-t-2 border-primary" />
          <div className="absolute right-4 top-4 h-8 w-8 rounded-tr-lg border-r-2 border-t-2 border-primary" />
          <div className="absolute bottom-4 left-4 h-8 w-8 rounded-bl-lg border-b-2 border-l-2 border-primary" />
          <div className="absolute bottom-4 right-4 h-8 w-8 rounded-br-lg border-b-2 border-r-2 border-primary" />
        </div>

        {/* Manual Entry Toggle */}
        <Button
          variant="outline"
          className="w-full gap-2"
          onClick={() => setManualEntry(!manualEntry)}
        >
          <Keyboard className="h-4 w-4" />
          {manualEntry ? "Hide Manual Entry" : "Enter Barcode Manually"}
        </Button>

        {manualEntry && (
          <Card className="space-y-3 p-4">
            <p className="text-sm font-medium text-foreground">
              Enter barcode number
            </p>
            <Input
              placeholder="e.g. 5901234123457"
              value={barcode}
              onChange={e => setBarcode(e.target.value)}
              type="text"
              inputMode="numeric"
            />
            <Button
              className="w-full"
              disabled={!barcode.trim() || loading}
              onClick={() => handleScan(barcode)}
            >
              {loading ? "Looking up…" : "Look Up Product"}
            </Button>
          </Card>
        )}

        {/* Demo barcodes */}
        <Card className="p-4">
          <p className="mb-3 text-sm font-bold text-foreground">
            Try a demo barcode
          </p>
          <div className="flex flex-wrap gap-2">
            {["3017620422003", "5449000000996", "7622210449283"].map(code => (
              <Button
                key={code}
                variant="secondary"
                size="sm"
                onClick={() => handleScan(code)}
                disabled={loading}
              >
                {code}
              </Button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BarcodeScanner;
